import { Button, Actions } from 'braid-design-system';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  FINISH_RESUME,
  PERSONAL_DETAILS,
  CAREER_HISTORY,
  EDUCATION,
  SUMMARY,
  SKILLS,
} from 'src/components/Router';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { READ } from 'src/consts';
import { useAppConfig } from 'src/modules/appConfig';
import type { Card } from 'src/types';

import { isFieldPopulatedForResumeExport } from '../../Validation/utils';
import { setRouteAsTouched, setAllRoutesAsTouched } from '../../actionCreators';
import { useResumeData } from '../../data';
import type { CreateResumeRoute } from '../../types';

interface Props {
  name: CreateResumeRoute;
  children: React.ReactNode;
}

export const InvalidSectionButton = ({ name, children }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigateRoute();
  const resumeData = useResumeData();
  const { showFeature } = useAppConfig();
  const showPersonalSummary = showFeature('personal-summary');

  const setTouched = useCallback(() => {
    if (name === FINISH_RESUME) {
      dispatch(setAllRoutesAsTouched());
      const {
        firstName: firstNamePopulated,
        lastName: lastNamePopulated,
        phoneNumber: phoneNumberPopulated,
        careerHistory: careerHistoryPopulated,
        education: educationPopulated,
        skills: skillsPopulated,
        summary: summaryPopulated,
      } = isFieldPopulatedForResumeExport(resumeData, {
        isSummaryRequired: showPersonalSummary,
      });

      let card: Card | '' = '';

      if (!firstNamePopulated || !lastNamePopulated || !phoneNumberPopulated) {
        card = PERSONAL_DETAILS;
      } else if (!careerHistoryPopulated) {
        card = CAREER_HISTORY;
      } else if (!educationPopulated) {
        card = EDUCATION;
      } else if (!skillsPopulated) {
        card = SKILLS;
      } else if (!summaryPopulated) {
        card = SUMMARY;
      }
      if (card !== '') {
        navigate({
          card,
          mode: READ,
        });
      }
    } else {
      dispatch(setRouteAsTouched(name));
    }
  }, [dispatch, navigate, name, resumeData, showPersonalSummary]);

  return (
    <Actions>
      <Button
        tone="formAccent"
        variant="solid"
        onClick={setTouched}
        data={{
          automation:
            name === FINISH_RESUME
              ? 'finish-resume-button'
              : `${name}-next-button`,
        }}
      >
        {children}
      </Button>
    </Actions>
  );
};
